/**
 * sessionRegime.js
 *
 * Classifies the trading-day regime from intraday bars so a scalper can decide
 * whether to trade at size, cut size, or sit out — the single highest-value
 * process improvement for a directional/expansion trader who bleeds on chop.
 *
 * The classifier looks at the structure of the session so far:
 *   - ADX:               trend strength (>25 trend, <20 range)
 *   - Higher-highs / lower-lows count:   is price making structure or rotating?
 *   - Range expansion:   is each bar's range growing (expansion) or shrinking (grind)?
 *   - Net displacement vs path length:   efficiency ratio (Kaufman) — high = trend, low = chop
 *   - VWAP rejection count:  how often price rejected back through VWAP (range behaviour)
 *
 * Returns a verdict object the UI can render directly.
 */

// Kaufman Efficiency Ratio: |net move| / sum(|bar-to-bar moves|)
// 1.0 = perfectly directional, ~0 = pure chop
function efficiencyRatio(closes) {
  if (closes.length < 3) return null;
  const net = Math.abs(closes[closes.length - 1] - closes[0]);
  let path = 0;
  for (let i = 1; i < closes.length; i++) path += Math.abs(closes[i] - closes[i - 1]);
  return path > 0 ? net / path : 0;
}

// Count structural higher-highs and lower-lows over a rolling 3-bar pivot
function structureCount(bars) {
  let hh = 0, ll = 0, lastPivotHigh = null, lastPivotLow = null;
  for (let i = 2; i < bars.length; i++) {
    const a = bars[i - 2], b = bars[i - 1], c = bars[i];
    const ah = a.high ?? a.close, bh = b.high ?? b.close, ch = c.high ?? c.close;
    const al = a.low  ?? a.close, bl = b.low  ?? b.close, cl = c.low  ?? c.close;
    // pivot high at b
    if (bh > ah && bh > ch) {
      if (lastPivotHigh != null && bh > lastPivotHigh) hh++;
      lastPivotHigh = bh;
    }
    // pivot low at b
    if (bl < al && bl < cl) {
      if (lastPivotLow != null && bl < lastPivotLow) ll++;
      lastPivotLow = bl;
    }
  }
  return { hh, ll };
}

// Count VWAP rejections — times price crossed back through VWAP (rotation = range)
function vwapRejections(bars, vwap) {
  if (vwap == null) return 0;
  let crossings = 0, prevSide = null;
  for (const b of bars) {
    const side = b.close > vwap ? 'above' : 'below';
    if (prevSide && side !== prevSide) crossings++;
    prevSide = side;
  }
  return crossings;
}

// Range expansion: ratio of recent-third avg bar range to first-third avg bar range
function rangeExpansion(bars) {
  if (bars.length < 9) return null;
  const third = Math.floor(bars.length / 3);
  const avgRange = (slice) => slice.reduce((s, b) =>
    s + ((b.high ?? b.close) - (b.low ?? b.close)), 0) / slice.length;
  const first = avgRange(bars.slice(0, third));
  const last  = avgRange(bars.slice(-third));
  return first > 0 ? last / first : 1;
}

/**
 * Classify session regime.
 * @param {Array} bars  intraday bars [{high,low,close,...}]
 * @param {number} adx  current ADX value (from alert engine)
 * @param {number} vwap current session VWAP
 * @returns {Object} verdict
 */
export function classifySessionRegime(bars, adx, vwap) {
  if (!bars || bars.length < 10) {
    return {
      regime: 'forming',
      label: 'Forming',
      verdict: 'Wait — need ~30 min of price action to classify',
      action: 'WAIT',
      color: 'var(--faint)',
      confidence: 0,
      metrics: {},
    };
  }

  const closes = bars.map(b => b.close);
  const er      = efficiencyRatio(closes);          // 0–1
  const { hh, ll } = structureCount(bars);
  const rejections = vwapRejections(bars, vwap);
  const expansion  = rangeExpansion(bars);          // >1 expanding, <1 contracting
  const netMovePct = Math.abs((closes[closes.length - 1] - closes[0]) / closes[0]) * 100;

  // Scoring: each signal votes trend (+) or range (−)
  let trendScore = 0;
  const reasons = [];

  // ADX is the strongest signal
  if (adx != null) {
    if (adx >= 25)      { trendScore += 2; reasons.push(`ADX ${adx.toFixed(0)} — trending`); }
    else if (adx >= 20) { trendScore += 1; reasons.push(`ADX ${adx.toFixed(0)} — weak trend`); }
    else if (adx < 15)  { trendScore -= 2; reasons.push(`ADX ${adx.toFixed(0)} — strong range`); }
    else                { trendScore -= 1; reasons.push(`ADX ${adx.toFixed(0)} — ranging`); }
  }

  // Efficiency ratio
  if (er != null) {
    if (er >= 0.45)      { trendScore += 2; reasons.push(`Efficient move (ER ${er.toFixed(2)}) — directional`); }
    else if (er >= 0.30) { trendScore += 1; reasons.push(`Moderate efficiency (ER ${er.toFixed(2)})`); }
    else if (er < 0.18)  { trendScore -= 2; reasons.push(`Choppy path (ER ${er.toFixed(2)}) — rotation`); }
    else                 { trendScore -= 1; reasons.push(`Low efficiency (ER ${er.toFixed(2)})`); }
  }

  // Structure: clean HH or LL = trend; both present = rotation
  if (hh >= 2 && ll === 0)      { trendScore += 1; reasons.push(`${hh} higher-highs — uptrend structure`); }
  else if (ll >= 2 && hh === 0) { trendScore += 1; reasons.push(`${ll} lower-lows — downtrend structure`); }
  else if (hh >= 1 && ll >= 1)  { trendScore -= 1; reasons.push(`Mixed HH+LL — two-sided rotation`); }

  // VWAP rejections: many crossings = range
  if (rejections >= 5)      { trendScore -= 2; reasons.push(`${rejections} VWAP crossings — heavy rotation`); }
  else if (rejections >= 3) { trendScore -= 1; reasons.push(`${rejections} VWAP crossings`); }
  else if (rejections <= 1) { trendScore += 1; reasons.push(`Price holding one side of VWAP`); }

  // Range expansion
  if (expansion != null) {
    if (expansion >= 1.3)      { trendScore += 1; reasons.push(`Ranges expanding (${expansion.toFixed(1)}x)`); }
    else if (expansion <= 0.7) { trendScore -= 1; reasons.push(`Ranges contracting (${expansion.toFixed(1)}x) — grind`); }
  }

  // Final classification
  let regime, label, verdict, action, color;
  if (trendScore >= 3) {
    regime = 'trend'; label = 'TREND DAY'; action = 'TRADE';
    verdict = 'Expansion/trend conditions — trade with-trend, size up on confirmation';
    color = 'var(--up)';
  } else if (trendScore <= -3) {
    regime = 'range'; label = 'RANGE / CHOP'; action = 'SIT OUT';
    verdict = 'Rotation/grind — cut size hard or sit out. This is your worst environment';
    color = 'var(--down)';
  } else {
    regime = 'mixed'; label = 'MIXED / UNCLEAR'; action = 'SMALL';
    verdict = 'No clear edge — trade small starter size only, wait for confirmation';
    color = 'var(--warn)';
  }

  const confidence = Math.min(100, Math.round((Math.abs(trendScore) / 8) * 100));

  return {
    regime, label, verdict, action, color, confidence,
    trendScore,
    reasons,
    metrics: {
      adx: adx != null ? Number(adx.toFixed(1)) : null,
      efficiencyRatio: er != null ? Number(er.toFixed(2)) : null,
      higherHighs: hh,
      lowerLows: ll,
      vwapRejections: rejections,
      rangeExpansion: expansion != null ? Number(expansion.toFixed(2)) : null,
      netMovePct: Number(netMovePct.toFixed(2)),
    },
  };
}

/**
 * Suggested position size multiplier given regime + how far into the session.
 * Trend day fully confirmed → full size. Range/forming → small or zero.
 */
export function suggestedSizeMultiplier(verdict, minutesIntoSession) {
  if (!verdict || verdict.regime === 'forming') return 0.25;
  if (verdict.regime === 'range') return 0.25;          // quarter size or sit out
  if (verdict.regime === 'mixed') return 0.5;           // half size starter
  // trend day — ramp with confirmation over the session
  if (minutesIntoSession < 60) return 0.5;              // still confirming
  if (verdict.confidence >= 60) return 1.0;             // full size
  return 0.75;
}

// ── Multi-day coil detector ───────────────────────────────────────────────────
// Detects when a symbol is coiling in a tight multi-day band — the NVDA 217–225
// four-day pin scenario. Scalping a coil is a losing game; the edge is the BREAK.
// Works on DAILY bars.
//
// A coil is flagged when:
//   - The last N daily closes sit inside a tight band (range% below threshold)
//   - Price is compressed near the EMA9/EMA20 cluster (low EMA separation)
//   - Ranges are contracting or flat (not already expanding/breaking out)
//
function ema(values, period) {
  if (values.length < period) return null;
  const k = 2 / (period + 1);
  let e = values.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < values.length; i++) e = values[i] * k + e * (1 - k);
  return e;
}

export function detectCoil(dailyBars, opts = {}) {
  const lookback     = opts.lookback     ?? 5;     // days to examine
  const bandPctMax   = opts.bandPctMax   ?? 4.0;   // max % range to count as tight
  const emaSepMax    = opts.emaSepMax    ?? 1.0;   // max EMA9-EMA20 separation %

  if (!dailyBars || dailyBars.length < Math.max(lookback, 20)) {
    return { coiling: false, reason: 'insufficient daily data' };
  }

  const recent = dailyBars.slice(-lookback);
  const closes = dailyBars.map(b => b.close);

  // 1. Band tightness over the lookback window
  const highs = recent.map(b => b.high ?? b.close);
  const lows  = recent.map(b => b.low  ?? b.close);
  const bandHigh = Math.max(...highs);
  const bandLow  = Math.min(...lows);
  const mid      = (bandHigh + bandLow) / 2;
  const bandPct  = mid > 0 ? ((bandHigh - bandLow) / mid) * 100 : 999;

  // 2. EMA9 / EMA20 compression
  const ema9  = ema(closes, 9);
  const ema20 = ema(closes, 20);
  const emaSepPct = (ema9 != null && ema20 != null && ema20 > 0)
    ? Math.abs((ema9 - ema20) / ema20) * 100 : 999;

  // 3. Are closes staying inside the band (consecutive inside-range)?
  const insideCount = recent.filter(b =>
    b.close <= bandHigh && b.close >= bandLow).length;

  // 4. Range contraction — compare recent avg daily range vs prior window
  const dayRange = (b) => (b.high ?? b.close) - (b.low ?? b.close);
  const recentAvgRange = recent.reduce((s, b) => s + dayRange(b), 0) / recent.length;
  const priorWindow = dailyBars.slice(-(lookback * 2), -lookback);
  const priorAvgRange = priorWindow.length
    ? priorWindow.reduce((s, b) => s + dayRange(b), 0) / priorWindow.length : recentAvgRange;
  const contracting = recentAvgRange <= priorAvgRange * 1.05; // flat or shrinking

  const tightBand   = bandPct <= bandPctMax;
  const emaCluster  = emaSepPct <= emaSepMax;
  const coiling     = tightBand && emaCluster && contracting && insideCount >= lookback - 1;

  const currentClose = closes[closes.length - 1];
  const distToHigh   = bandHigh > 0 ? ((bandHigh - currentClose) / currentClose) * 100 : null;
  const distToLow    = currentClose > 0 ? ((currentClose - bandLow) / currentClose) * 100 : null;

  return {
    coiling,
    bandPct: Number(bandPct.toFixed(2)),
    bandHigh: Number(bandHigh.toFixed(2)),
    bandLow: Number(bandLow.toFixed(2)),
    emaSepPct: Number(emaSepPct.toFixed(2)),
    ema9: ema9 != null ? Number(ema9.toFixed(2)) : null,
    ema20: ema20 != null ? Number(ema20.toFixed(2)) : null,
    daysInBand: insideCount,
    lookback,
    contracting,
    distToBreakUp: distToHigh != null ? Number(distToHigh.toFixed(2)) : null,
    distToBreakDn: distToLow != null ? Number(distToLow.toFixed(2)) : null,
    verdict: coiling
      ? `Coiling ${insideCount}d in ${bandPct.toFixed(1)}% band — STAND DOWN, wait for break above $${bandHigh.toFixed(2)} or below $${bandLow.toFixed(2)}`
      : tightBand && !contracting
        ? `Band tight but ranges expanding — break may be starting`
        : 'No coil — normal range',
  };
}

// ── Pre-market / prior-day analysis ───────────────────────────────────────────
// Before the open there's no intraday structure, so we classify what we CAN know:
// prior-day trend, coil status, overnight gap, and daily ADX. Feeds the
// "first decision" gate so you arrive with a committed plan.
export function analyzePreMarket(dailyBars, preMarketPrice) {
  if (!dailyBars || dailyBars.length < 20) {
    return { ready: false, reason: 'Insufficient daily history' };
  }
  const closes = dailyBars.map(b => b.close);
  const lastBar = dailyBars[dailyBars.length - 1];
  const prevClose = lastBar.close;

  const coil = detectCoil(dailyBars);

  const priorRange = (lastBar.high ?? prevClose) - (lastBar.low ?? prevClose);
  const priorBody  = lastBar.close - (lastBar.open ?? lastBar.close);
  const priorDir   = priorBody > priorRange * 0.2 ? 'up'
                   : priorBody < -priorRange * 0.2 ? 'down' : 'flat';

  const dailyADX = dailyADXValue(dailyBars);

  let gapPct = null, gapNote = null;
  if (preMarketPrice != null && prevClose > 0) {
    gapPct = ((preMarketPrice - prevClose) / prevClose) * 100;
    gapNote = Math.abs(gapPct) >= 0.5
      ? `${gapPct > 0 ? 'Gap up' : 'Gap down'} ${Math.abs(gapPct).toFixed(2)}% — expect ${Math.abs(gapPct) >= 1 ? 'volatility, possible trend day' : 'mild directional bias'}`
      : 'Flat open — no overnight edge';
  }

  const ema9  = closes.length >= 9  ? ema(closes, 9)  : null;
  const ema20 = closes.length >= 20 ? ema(closes, 20) : null;
  const dailyTrend = (ema9 != null && ema20 != null)
    ? (ema9 > ema20 * 1.005 ? 'bullish' : ema9 < ema20 * 0.995 ? 'bearish' : 'flat')
    : 'unknown';

  let stance, stanceColor, stanceNote;
  if (coil.coiling) {
    stance = 'WAIT FOR BREAK'; stanceColor = 'var(--down)';
    stanceNote = `${coil.daysInBand}-day coil in ${coil.bandPct}% band. Do NOT scalp the pin. Wait for break above $${coil.bandHigh} or below $${coil.bandLow}, then trade the break direction.`;
  } else if (gapPct != null && Math.abs(gapPct) >= 1) {
    stance = 'TRADE WITH-TREND'; stanceColor = 'var(--up)';
    stanceNote = `Large gap suggests an expansion/trend day. Trade with-trend near-the-money once the open confirms direction. Don't fade the gap.`;
  } else if (dailyADX != null && dailyADX >= 25) {
    stance = 'TRADE WITH-TREND'; stanceColor = 'var(--up)';
    stanceNote = `Daily trend strong (ADX ${dailyADX.toFixed(0)}, ${dailyTrend}). Favour with-trend entries; don't fight the daily direction.`;
  } else if (dailyADX != null && dailyADX < 18) {
    stance = 'SMALL / SELECTIVE'; stanceColor = 'var(--warn)';
    stanceNote = `Daily is ranging (ADX ${dailyADX.toFixed(0)}). Recent days choppy — start small, let the first 30 min confirm before sizing up.`;
  } else {
    stance = 'WAIT FOR CONFIRMATION'; stanceColor = 'var(--warn)';
    stanceNote = `No clear pre-market edge. Let the first 30 minutes classify the regime before committing size.`;
  }

  const priorHigh = lastBar.high  != null ? Number(lastBar.high.toFixed(2))  : null;
  const priorLow  = lastBar.low   != null ? Number(lastBar.low.toFixed(2))   : null;
  const refPrice  = preMarketPrice ?? prevClose;
  const distToHighPct = (priorHigh != null && refPrice > 0)
    ? Number(((priorHigh - refPrice) / refPrice * 100).toFixed(2)) : null;
  const distToLowPct  = (priorLow  != null && refPrice > 0)
    ? Number(((refPrice - priorLow)  / refPrice * 100).toFixed(2)) : null;

  return {
    ready: true, coil,
    prevClose: Number(prevClose.toFixed(2)),
    priorDir,
    priorHigh, priorLow, distToHighPct, distToLowPct,
    dailyADX: dailyADX != null ? Number(dailyADX.toFixed(1)) : null,
    dailyTrend,
    gapPct: gapPct != null ? Number(gapPct.toFixed(2)) : null,
    gapNote, stance, stanceColor, stanceNote,
  };
}

function dailyADXValue(bars) {
  const P = 14;
  if (bars.length < P * 2) return null;
  const slice = bars.slice(-(P * 2));
  const pDM = [], mDM = [], tr = [];
  for (let i = 1; i < slice.length; i++) {
    const h = slice[i].high ?? slice[i].close, l = slice[i].low ?? slice[i].close;
    const ph = slice[i-1].high ?? slice[i-1].close, pl = slice[i-1].low ?? slice[i-1].close;
    const pc = slice[i-1].close;
    const up = h - ph, dn = pl - l;
    pDM.push(up > dn && up > 0 ? up : 0);
    mDM.push(dn > up && dn > 0 ? dn : 0);
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  const wilder = (arr) => {
    let s = arr.slice(0, P).reduce((a,b)=>a+b,0); const r=[s];
    for (let i=P;i<arr.length;i++){ s=s-s/P+arr[i]; r.push(s); } return r;
  };
  const atr=wilder(tr), p=wilder(pDM), m=wilder(mDM);
  const dx = atr.map((a,i)=>{ if(!a)return 0; const pi=p[i]/a*100,mi=m[i]/a*100,s=pi+mi; return s?Math.abs(pi-mi)/s*100:0; });
  return dx.slice(-P).reduce((a,b)=>a+b,0)/P;
}
